import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Label,
  ScrollArea,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Text,
  useIsMobile,
} from "@salestrak/ui";
import {
  WorkflowSelector,
  StageSelector,
} from "#app/components/entity-selectors";
import { PageHeader, PageHeaderBack, PageHeaderTitle } from "#app/components/page-header";
import { SettingsGroup } from "./settings-group";
import { type WebhookItem } from "#app/hooks/settings/use-webhook-mutations";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type { CreateWebhookInput } from "#app/lib/data";

const HTTP_METHODS = [
  { label: "GET", value: "get" },
  { label: "POST", value: "post" },
  { label: "PUT", value: "put" },
  { label: "PATCH", value: "patch" },
  { label: "DELETE", value: "delete" },
];

const EVENT_SOURCES = [{ label: "Projects", value: "issues" }];

const TRIGGERS = [
  { label: "Project Stage Change", value: "issueStageChange" },
  { label: "Project Created On Stage", value: "issueCreatedOnStage" },
  { label: "Project Deleted", value: "issueDeleted" },
  { label: "Project Trashed", value: "issueTrashed" },
];

const JOIN_OPTIONS = [
  { key: "customFields", label: "Custom Fields" },
  { key: "createdBy", label: "Created By" },
  { key: "lastUpdatedBy", label: "Last Updated By" },
] as const;

// Base UI's Select is value-based (no `{value,label}` Option object), so the
// trigger renders the label by looking the value up in its own option list.
function optionLabel(
  options: readonly { value: string; label: string }[],
  val: string | null | undefined,
  fallback: string,
): string {
  return options.find((o) => o.value === val)?.label ?? fallback;
}

interface WebhookFormViewProps {
  webhook: WebhookItem | null;
  onCancel: () => void;
  onSubmit: (data: CreateWebhookInput) => Promise<void>;
}

export function WebhookFormView({ webhook, onCancel, onSubmit }: WebhookFormViewProps) {
  const isMobile = useIsMobile();
  const isEditing = !!webhook;
  const { stageMap } = useWorkspaceData();

  const savedStageId = (webhook?.when as any)?.stageId ?? null;
  const savedWorkflowId =
    typeof savedStageId === "string"
      ? (stageMap.get(savedStageId)?.workflowId ?? null)
      : null;

  const [name, setName] = useState(webhook?.name ?? "");
  const [url, setUrl] = useState(webhook?.url ?? "");
  const [method, setMethod] = useState(webhook?.method ?? "post");
  const [resource, setResource] = useState(webhook?.resource ?? "issues");
  const [trigger, setTrigger] = useState(webhook?.trigger ?? "issueStageChange");
  const [stageId, setStageId] = useState<string | null>(savedStageId);
  const [workflowId, setWorkflowId] = useState<string | null>(savedWorkflowId);
  const [joins, setJoins] = useState<Record<string, boolean>>(
    (webhook?.joins as Record<string, boolean>) ?? {},
  );
  const [submitting, setSubmitting] = useState(false);

  const showTarget = trigger !== "issueDeleted" && trigger !== "issueTrashed";

  const isValid = url.trim() !== "" && method && resource && trigger &&
    (showTarget ? !!stageId : true);

  function handleWorkflowChange(ids: string[]) {
    const id = ids[0] ?? null;
    setWorkflowId(id);
    setStageId(null);
  }

  function handleStageChange(id: string) {
    setStageId(id);
  }

  function handleJoinToggle(key: string) {
    setJoins((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const when: Record<string, any> = {};
      if (trigger !== "issueDeleted" && trigger !== "issueTrashed" && stageId) {
        when.stageId = stageId;
      }

      const activeJoins: Record<string, boolean> = {};
      for (const [k, v] of Object.entries(joins)) {
        if (v) activeJoins[k] = true;
      }

      await onSubmit({
        name,
        url,
        method,
        resource,
        trigger,
        when,
        joins: activeJoins,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderBack label="API" onClick={onCancel} />
          <PageHeaderTitle>{isEditing ? "Edit webhook" : "Create webhook"}</PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box
            as="form"
            gap="xl"
            fill="width"
            maxWidth={800}
            onSubmit={(e) => {
              e.preventDefault();
              if (isValid && !submitting) void handleSubmit();
            }}
          >

          <SettingsGroup label="Settings">
            <Box gap="md" padding="md">
              <Field>
                <FieldLabel>Label</FieldLabel>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter a descriptive name..."
                />
              </Field>

              <Field>
                <FieldLabel>URL</FieldLabel>
                <Box direction={isMobile ? "column" : "row"} gap="sm" fill="width">
                  <Box>
                    <Select
                      value={method}
                      onValueChange={(next: string | null) => {
                        if (next) setMethod(next);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Method">
                          {(value: string) =>
                            optionLabel(HTTP_METHODS, value, "Method")
                          }
                        </SelectValue>
                      </SelectTrigger>
                      <SelectPopup>
                        {HTTP_METHODS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectPopup>
                    </Select>
                  </Box>
                  <Box flex={1}>
                    <Input
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="https://..."
                    />
                  </Box>
                </Box>
              </Field>

              <Field>
                <FieldLabel>Event source</FieldLabel>
                <FieldDescription>
                  What is the root document you want to trigger off of
                </FieldDescription>
                <Box fill="width">
                  <Select
                    value={resource}
                    onValueChange={(next: string | null) => {
                      if (next) setResource(next);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select...">
                        {(value: string) =>
                          optionLabel(EVENT_SOURCES, value, "Select...")
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectPopup>
                      {EVENT_SOURCES.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectPopup>
                  </Select>
                </Box>
              </Field>

              <Field>
                <FieldLabel>Trigger</FieldLabel>
                <Box fill="width">
                  <Select
                    value={trigger}
                    onValueChange={(next: string | null) => {
                      if (next) {
                        setTrigger(next);
                        if (next === "issueDeleted" || next === "issueTrashed") {
                          setStageId(null);
                          setWorkflowId(null);
                        }
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select...">
                        {(value: string) =>
                          optionLabel(TRIGGERS, value, "Select...")
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectPopup>
                      {TRIGGERS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectPopup>
                  </Select>
                </Box>
              </Field>

              {showTarget ? (
                <Box gap="xs">
                  <Text size="sm" weight="medium">Target</Text>
                  <Box direction={isMobile ? "column" : "row"} gap="sm" fill="width">
                    <Box flex={1}>
                      {/* The concrete selectors ship their own trigger and
                          content children; `contentFit` sizes the popup to
                          the trigger. */}
                      <WorkflowSelector
                        value={workflowId}
                        onValueChange={handleWorkflowChange}
                        placeholder="Workflow..."
                        variant="popover"
                        contentFit
                        fill
                      />
                    </Box>
                    {workflowId ? (
                      <Box flex={1}>
                        <StageSelector
                          workflowId={workflowId}
                          value={stageId}
                          onValueChange={handleStageChange}
                          placeholder="Stage..."
                          variant="popover"
                        />
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              ) : null}

              <Box gap="xs">
                <Text size="sm" weight="medium">Joins</Text>
                <Text size="xs" variant="muted">
                  What related information would you like to send with
                </Text>
                <Box gap="xs">
                  {JOIN_OPTIONS.map((join) => (
                    <Label key={join.key}>
                      <Checkbox
                        checked={joins[join.key] ?? false}
                        onCheckedChange={() => handleJoinToggle(join.key)}
                      />
                      <Text size="sm" weight="normal">
                        {join.label}
                      </Text>
                    </Label>
                  ))}
                </Box>
              </Box>
            </Box>
          </SettingsGroup>

          <Box direction="row" gap="md" justify="end">
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || submitting}>
              {isEditing ? "Save" : "Create"} Webhook
            </Button>
          </Box>
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}
